/**
 * ArrivalCinematic — NAV-03 camera damp from spline-endpoint to walk-ready pose.
 *
 * Plan 05-02 Task 1. Replaces the Phase 3/5-01 `case 'arrivalCinematic': return null`
 * stub in NavRig with a real 2-second cinematic that smoothly transitions the
 * camera from its StreetcarRig end pose (last spline waypoint at [40, 2, -260]
 * in the production manifest) to the walk-ready pose authored at
 * `manifest.spawnPose` (180° Y-rotated so the visitor faces the pavilion,
 * not the direction of travel).
 *
 * Contract (05-RESEARCH.md Pattern 2):
 *   - On first useFrame tick, capture the current camera pose into a ref
 *     (this is the START pose — effectively the StreetcarRig's final pose).
 *   - Accumulate elapsed via useFrame's delta (seconds).
 *   - Compute t = min(elapsed / ARRIVAL_DURATION_S, 1); apply easeInOutQuad.
 *   - Position: THREE.Vector3.lerp (startPos → targetPos, eased).
 *   - Quaternion: THREE.Quaternion.slerp (startQuat → targetQuat, eased) —
 *     NOT component-wise lerp (05-RESEARCH.md Don't-Hand-Roll row 7).
 *   - Write pose to useNavStore every frame (Minimap / DebugHUD read this).
 *   - Fire ARRIVAL_DONE via useNavSend exactly once when t reaches 1
 *     (send-once guard ref; protects against React StrictMode double-invoke
 *     and useFrame ticks past t=1 before the machine transitions us out).
 *
 * Target pose source: manifest.spawnPose is the single source of truth
 * (05-RESEARCH.md Pitfall 7 spirit — content in manifest, not in code).
 * Defaults to origin + identity quat if author omits the field.
 *
 * AudioListener is mounted elsewhere (Plan 05-05 AudioListenerBinding);
 * this rig focuses purely on motion.
 */
import { useFrame, useThree } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useManifest } from '../content/useManifest';
import { useNavSend } from './NavContext';
import { useNavStore } from '../store/useNavStore';

/** Arrival cinematic total duration in seconds. 2s per plan spec / 05-RESEARCH.md Pattern 2. */
export const ARRIVAL_DURATION_S = 2;

/**
 * easeInOutQuad — symmetric S-curve easing.
 *
 * t < 0.5 → 2*t*t             (quadratic ease-in, starts slow)
 * t ≥ 0.5 → 1 - (-2t + 2)²/2   (quadratic ease-out, ends slow)
 *
 * Verbatim from 05-RESEARCH.md Pattern 2 (line 235). 2-liner avoids the
 * @tweenjs/tween.js dependency (Don't-Hand-Roll row 8 — tween.js would be
 * overkill for a single easing curve applied in one place).
 */
export function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

export function ArrivalCinematic() {
  const { camera } = useThree();
  const send = useNavSend();
  const { manifest } = useManifest();

  // Target pose comes from manifest.spawnPose. useMemo keeps allocation out
  // of the useFrame hot path. Reads re-run only if manifest identity changes.
  const { targetPos, targetQuat } = useMemo(() => {
    const pose = manifest?.spawnPose ?? {
      position: [0, 1.7, 0] as [number, number, number],
      quaternion: [0, 0, 0, 1] as [number, number, number, number],
    };
    return {
      targetPos: new THREE.Vector3(...pose.position),
      targetQuat: new THREE.Quaternion(...pose.quaternion),
    };
  }, [manifest?.spawnPose]);

  // Start-pose capture. Populated on the first useFrame tick so the camera
  // pose we lerp FROM is exactly whatever StreetcarRig's last frame left
  // the camera at. Using useRef rather than useState avoids re-renders.
  const startPoseRef = useRef<{
    pos: THREE.Vector3;
    quat: THREE.Quaternion;
  } | null>(null);

  const elapsedRef = useRef(0);
  // Send-once guard — prevents double ARRIVAL_DONE dispatches when useFrame
  // continues to tick after t=1 (StrictMode double-invoke, transition-latency
  // between send and the machine exiting this state).
  const arrivalFiredRef = useRef(false);

  // Reusable scratch allocations — avoids per-frame GC pressure.
  const scratchPos = useRef(new THREE.Vector3());
  const scratchQuat = useRef(new THREE.Quaternion());

  useFrame((_state, delta) => {
    // Capture start pose on first tick. Until this is set, we cannot lerp.
    if (startPoseRef.current === null) {
      startPoseRef.current = {
        pos: camera.position.clone(),
        quat: camera.quaternion.clone(),
      };
    }

    elapsedRef.current += delta;
    const t = Math.min(elapsedRef.current / ARRIVAL_DURATION_S, 1);
    const eased = easeInOutQuad(t);

    // Position: lerp (component-wise is correct for translation).
    scratchPos.current.copy(startPoseRef.current.pos).lerp(targetPos, eased);
    camera.position.copy(scratchPos.current);

    // Rotation: slerp (SPHERICAL linear interpolation — preserves unit length
    // of the quaternion and produces the shortest-arc rotation path). Component
    // lerp between two quaternions is NOT the same interpolation and produces
    // noticeable drift on large rotations like the 180° Y we use here.
    scratchQuat.current.copy(startPoseRef.current.quat).slerp(targetQuat, eased);
    camera.quaternion.copy(scratchQuat.current);

    // Persist pose to nav store so Minimap + DebugHUD can read it.
    useNavStore.getState().setCameraPose(
      [camera.position.x, camera.position.y, camera.position.z],
      [
        camera.quaternion.x,
        camera.quaternion.y,
        camera.quaternion.z,
        camera.quaternion.w,
      ],
    );

    // Fire ARRIVAL_DONE once at t=1. Machine will transition us to freeWalk
    // and this component will unmount; the guard is defense in depth against
    // the narrow window between send() and unmount.
    if (t >= 1 && !arrivalFiredRef.current) {
      arrivalFiredRef.current = true;
      send({ type: 'ARRIVAL_DONE' });
    }
  });

  return null; // pure side-effect rig — no 3D output of its own
}
